import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {AudioItem, AudioItemObject} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import {createAudioItemFromObject} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import type {AudioData} from '@Libraries/add-media-library';
import {getUniqueString} from '@Utils/string.util';
import {getAudioUrl} from '@Libraries/user-audio-library';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import type {DeepPartial} from '@/global';

const ADDING_AUDIO_LOADING_KEY = 'addingAudioItemsData';

export interface AudioClipsObject {
  audioItemsHashMap: Record<string, AudioItemObject>;
  selectedAudioItemUID: string;
}

export interface AddAudioData extends AudioData {
  uid?: string;
  onPageStartTime?: number;
}

interface TrimAudioItemOpts {
  undoable?: boolean;
}

interface AddAudioItemsOpts {
  undoable?: boolean;
  updateRedux?: boolean;
  selectLastAudioItem?: boolean;
  selectOnAdd?: boolean;
}

export class AudioClips extends CommonMethods {
  public audioItemsHashMap: Record<string, AudioItem> = {};
  public selectedAudioItemUID = '';
  public page: Page;

  public constructor(page: Page) {
    super();
    this.page = page;
  }

  public hasActiveSelection(): boolean {
    return this.selectedAudioItemUID !== '';
  }

  public hasAudio(): boolean {
    return Object.keys(this.audioItemsHashMap).length !== 0;
  }

  public selectAudioItem(uid: string): void {
    this.page.poster.clearItemSelection();
    this.selectedAudioItemUID = uid;
    this.page.poster.redux.updateSelectedAudioItemUID();
  }

  public selectFirstAudioItem(): void {
    const firstAudioItem = this.getMinOnPageStartTimeAudioItem();
    if (firstAudioItem) {
      this.selectAudioItem(firstAudioItem.uid);
    }
  }

  public unselectAudioPlaylist(): void {
    if (this.selectedAudioItemUID) {
      this.selectedAudioItemUID = '';
      this.page.poster.redux.updateSelectedAudioItemUID();
    }
  }

  public toObject(): AudioClipsObject {
    const audioItemObjects: Record<string, AudioItemObject> = {};

    for (const [key, audioItem] of Object.entries(this.audioItemsHashMap)) {
      audioItemObjects[key] = audioItem.toObject();
    }

    return {
      audioItemsHashMap: audioItemObjects,
      selectedAudioItemUID: this.selectedAudioItemUID,
    };
  }

  public getSelectedAudioItem(): AudioItem | undefined {
    return this.selectedAudioItemUID ? this.audioItemsHashMap[this.selectedAudioItemUID] : undefined;
  }

  public deleteSelectedAudioItem(): void {
    const audioItem = this.getSelectedAudioItem();
    if (audioItem) {
      this.doRemoveItem(audioItem.uid);
      this.recompactTimelineSubRows();
      if (this.selectedAudioItemUID) {
        const minOnPageStartTimeAudioItem = this.getMinOnPageStartTimeAudioItem();
        this.selectedAudioItemUID = minOnPageStartTimeAudioItem ? minOnPageStartTimeAudioItem.uid : '';
      }
      this.page.poster.fire('modified');
      this.page.poster.redux.updateReduxData();
      this.page.poster.history.addPosterHistory();
      this.updatePosterTimeIfNeeded();
    }
  }

  public updatePosterTimeIfNeeded(): void {
    if (!this.page.poster.isVideo()) {
      void this.page.poster.stop();
    }
  }

  public async replaceAudioItem(audioItemIdToReplace: string, audioItemToAdd: AudioData): Promise<void> {
    const {onPageStartTime} = this.audioItemsHashMap[audioItemIdToReplace];
    const uid = getUniqueString();
    await this.addAudioItemsData(
      [
        {
          ...audioItemToAdd,
          uid,
          onPageStartTime,
        },
      ],
      {
        updateRedux: false,
        undoable: false,
        selectLastAudioItem: false,
      }
    );
    this.selectedAudioItemUID = uid;
    this.doRemoveItem(audioItemIdToReplace);
    this.recompactTimelineSubRows();
    await this.page.poster.seekPosterToSelectedAudioItem();
    this.page.calculateAndUpdatePageDuration();
    this.page.poster.history.addPosterHistory();
    this.page.poster.redux.updateReduxData();
  }

  public async addAudioItemsData(audioItemsData: AddAudioData[], {updateRedux = true, undoable = true, selectOnAdd = true}: AddAudioItemsOpts = {}): Promise<void> {
    showLoading(ADDING_AUDIO_LOADING_KEY, {
      text: audioItemsData.length > 1 ? window.i18next.t('pmwjs_loading_audio_clips') : window.i18next.t('pmwjs_loading_audio_clip'),
    });

    try {
      await Promise.all(audioItemsData.map((audioItemData) => this.addAudioItemData(audioItemData, selectOnAdd)));

      this.page.calculateAndUpdatePageDuration();
      if (undoable) {
        this.page.poster.history.addPosterHistory();
      }
      if (updateRedux) {
        this.page.poster.redux.updateReduxData();
      }
    } finally {
      hideLoading(ADDING_AUDIO_LOADING_KEY);
    }
  }

  private async addAudioItemData(audioItemData: AddAudioData, selectOnAdd = true): Promise<void> {
    const minStartTimeBuffer = 1;
    let onPageStartTime = audioItemData.onPageStartTime ?? this.page.clock.getCurrentTime();
    const pageDuration = this.page.getDuration();
    if (onPageStartTime > pageDuration - minStartTimeBuffer) {
      onPageStartTime = Math.max(0, pageDuration - minStartTimeBuffer);
    }
    const uid = audioItemData.uid ?? getUniqueString();

    const audioItem = await createAudioItemFromObject(this, {
      uid,
      hashedFilename: audioItemData.filename,
      onPageStartTime,
      audioPlayer: {
        audioUrl: getAudioUrl(audioItemData.filename, audioItemData.src),
        originalDuration: audioItemData.duration,
        trim: {
          isTrimmed: false,
          startTime: 0,
          endTime: audioItemData.duration,
        },
      },
      source: audioItemData.src,
      name: audioItemData.name,
    });

    this.audioItemsHashMap[audioItem.uid] = audioItem;
    if (selectOnAdd) {
      this.selectedAudioItemUID = uid;
    }
  }

  public async trimAudioItem(audioItemId: string, startTime: number, endTime: number, {undoable = true}: TrimAudioItemOpts = {}): Promise<void> {
    const audioItem = this.getAudioItemForId(audioItemId);
    if (audioItem) {
      await audioItem.updateFromObject(
        {
          audioPlayer: {
            trim: {
              isTrimmed: true,
              startTime,
              endTime,
            },
          },
        },
        {
          undoable,
          updateRedux: true,
        }
      );
    }
  }

  public getAudioItemForId(audioItemId: string): AudioItem | undefined {
    return this.audioItemsHashMap[audioItemId];
  }

  public async updateFromObject(
    audioClipsObject: DeepPartial<AudioClipsObject> = {},
    {updateRedux = true, fireModifiedEvent = true, undoable = true, recompactSubRows = true}: UpdateFromObjectOpts = {}
  ): Promise<void> {
    const {audioItemsHashMap, selectedAudioItemUID: _selectedAudioItemUID, ...otherAudioPlaylistItemsObject} = audioClipsObject;
    this.copyVals({
      ...otherAudioPlaylistItemsObject,
    });
    // delete items that are in this class but not in the audioClipsObject
    if (audioItemsHashMap) {
      // Update/Add items from audioClipsObject
      const updateFromObjectPromises = [];

      const newAudioItemsHashmap: Record<string, AudioItem> = {};
      const addItemPromises = [];

      for (const [uid, audioItemObject] of Object.entries(audioItemsHashMap)) {
        if (audioItemObject) {
          if (this.audioItemsHashMap[uid]) {
            newAudioItemsHashmap[uid] = this.audioItemsHashMap[uid];
            updateFromObjectPromises.push(
              this.audioItemsHashMap[uid].updateFromObject(audioItemObject, {
                undoable: false,
                updateRedux: false,
                fireEvent: false,
              })
            );
          } else {
            addItemPromises.push(createAudioItemFromObject(this, audioItemObject));
          }
        }
      }

      const itemsToAdd = await Promise.all(addItemPromises);
      for (const itemToAdd of itemsToAdd) {
        newAudioItemsHashmap[itemToAdd.uid] = itemToAdd;
      }

      for (const [uid] of Object.entries(this.audioItemsHashMap)) {
        if (audioItemsHashMap[uid] === undefined) {
          if (this.selectedAudioItemUID === uid) {
            this.selectedAudioItemUID = '';
          }
          this.audioItemsHashMap[uid].onRemove();
        }
      }

      this.audioItemsHashMap = newAudioItemsHashmap;
      await Promise.all(updateFromObjectPromises);
    }

    if (recompactSubRows) {
      this.recompactTimelineSubRows();
    }
    if (undoable) {
      this.page.poster.history.addPosterHistory();
    }
    if (fireModifiedEvent) {
      this.page.poster.fire('modified');
    }
    if (updateRedux) {
      this.page.poster.redux.updateReduxData();
    }
  }

  public getDuration(): number {
    return this.getMaxOnPageEndTime() - this.getMinOnPageStartTime();
  }

  public getMaxOnPageEndTime(): number {
    const lastAudioItem = this.getMaxOnPageEndTimeAudioItem();
    return lastAudioItem ? lastAudioItem.getOnPageEndTime() : 0;
  }

  private getMaxOnPageEndTimeAudioItem(): AudioItem | undefined {
    let maxOnPageEndTimeAudioItem: AudioItem | undefined = undefined;

    for (const [, audioItem] of Object.entries(this.audioItemsHashMap)) {
      maxOnPageEndTimeAudioItem ??= audioItem;

      if (audioItem.getOnPageEndTime() > maxOnPageEndTimeAudioItem.getOnPageEndTime()) {
        maxOnPageEndTimeAudioItem = audioItem;
      }
    }

    if (maxOnPageEndTimeAudioItem === undefined) {
      return undefined;
    }

    return maxOnPageEndTimeAudioItem;
  }

  public async trimAudiosExceedingPageDuration(minDurationThreshold = 1, fireModifiedEvent = true): Promise<void> {
    const pageDuration = this.page.getDuration();
    const newAudioItemsHashMap: Record<string, AudioItemObject> = {};
    for (const [key, audioItem] of Object.entries(this.audioItemsHashMap)) {
      if (audioItem.getOnPageEndTime() > pageDuration) {
        const endTime = audioItem.getEndTimeAccordingToPageDuration(pageDuration);
        if ((endTime - audioItem.audioPlayer.trim.startTime) / audioItem.audioPlayer.speed < minDurationThreshold) {
          continue;
        }

        newAudioItemsHashMap[key] = {
          ...audioItem.toObject(),
          audioPlayer: {
            ...audioItem.toObject().audioPlayer,
            trim: {
              ...audioItem.toObject().audioPlayer.trim,
              isTrimmed: true,
              endTime,
            },
          },
        };
      } else {
        newAudioItemsHashMap[key] = audioItem.toObject();
      }
    }

    await this.updateFromObject(
      {
        audioItemsHashMap: newAudioItemsHashMap,
      },
      {undoable: false, fireModifiedEvent}
    );
  }

  private getMinOnPageStartTimeAudioItem(): AudioItem | undefined {
    let minOnPageStartTimeAudioItem: AudioItem | undefined = undefined;

    for (const [, audioItem] of Object.entries(this.audioItemsHashMap)) {
      minOnPageStartTimeAudioItem ??= audioItem;

      if (audioItem.onPageStartTime < minOnPageStartTimeAudioItem.onPageStartTime) {
        minOnPageStartTimeAudioItem = audioItem;
      }
    }

    if (minOnPageStartTimeAudioItem === undefined) {
      return undefined;
    }

    return minOnPageStartTimeAudioItem;
  }

  private getMinOnPageStartTime(): number {
    const firstAudioItem = this.getMinOnPageStartTimeAudioItem();
    return firstAudioItem ? firstAudioItem.onPageStartTime : 0;
  }

  public play(): void {
    for (const [, audioItem] of Object.entries(this.audioItemsHashMap)) {
      audioItem.play();
    }
  }

  public getTimeForAudioItem(audioItemId: string): number {
    return this.audioItemsHashMap[audioItemId].onPageStartTime + 0.01;
  }

  public pause(): void {
    for (const [, audioItem] of Object.entries(this.audioItemsHashMap)) {
      audioItem.audioPlayer.pause();
    }
  }

  public async seekToPageTime(pageTime: number): Promise<void> {
    const promises = [];
    for (const [, audioItem] of Object.entries(this.audioItemsHashMap)) {
      promises.push(audioItem.seekToPageTime(pageTime));
    }
    await Promise.all(promises);
  }

  public recompactTimelineSubRows(): void {
    const entries = Object.entries(this.audioItemsHashMap);
    if (entries.length === 0) {
      return;
    }

    const pageDuration = this.page.getDuration();

    const items = entries.map(([id, audio]) => ({
      id,
      startTime: audio.onPageStartTime,
      endTime: Math.min(audio.onPageStartTime + audio.audioPlayer.getPlaybackDuration(), pageDuration),
      subRow: 0,
    }));

    const sorted = [...items].sort((a, b) => a.startTime - b.startTime);
    const result: {id: string; startTime: number; endTime: number; subRow: number}[] = [];

    for (const item of sorted) {
      let row = 0;
      while (result.some((existing) => existing.subRow === row && this.#detectOverlap(existing, item))) {
        row += 1;
      }
      result.push({...item, subRow: row});
    }

    for (const item of result) {
      const audio = this.audioItemsHashMap[item.id];
      if (audio) {
        audio.timelineSubRow = item.subRow;
      }
    }
  }

  #detectOverlap(item1: {id: string; startTime: number; endTime: number}, item2: {id: string; startTime: number; endTime: number}): boolean {
    if (item1.id === item2.id) {
      return false;
    }
    return item1.startTime < item2.endTime && item1.endTime > item2.startTime;
  }

  private doRemoveItem(uid: string): void {
    this.audioItemsHashMap[uid].onRemove();
    const {[uid]: _, ...audioItemsHashMap} = this.audioItemsHashMap;
    this.audioItemsHashMap = audioItemsHashMap;
  }

  public duplicateAudio(audioUID: string): void {
    const audioItem = this.getAudioItemForId(audioUID);
    if (!audioItem) {
      return;
    }
    void this.page.items.addItems.addAudioItemFromAnotherAudioItem(audioItem);
  }
}
